import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { InternalChatMensagem, MensagemStatus } from '../../types';
import { canApplyMensagemStatus } from '../../utils/mensagemStatusRank';
import {
  enrichRealtimeMessages,
  fetchMessageHistory,
  resendTemplate,
  resumeConversationTemplate,
  sendMessage,
} from './actions';

interface MessagesByConversa {
  items: InternalChatMensagem[];
  nextCursor: string | null;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
  historyHydrated: boolean;
}

interface MessagesState {
  byConversaId: Record<string, MessagesByConversa>;
  isSending: boolean;
  sendError: string | null;
}

const initialState: MessagesState = {
  byConversaId: {},
  isSending: false,
  sendError: null,
};

const emptyEntry = (): MessagesByConversa => ({
  items: [],
  nextCursor: null,
  hasMore: false,
  isLoading: false,
  error: null,
  historyHydrated: false,
});

const messagesSlice = createSlice({
  name: 'chatAtendimentoMessages',
  initialState,
  reducers: {
    clearSendError: (state) => {
      state.sendError = null;
    },
    resetConversaMessages: (state, action: { payload: string }) => {
      delete state.byConversaId[action.payload];
    },
    realtimeMessageCreated: (state, action: PayloadAction<InternalChatMensagem>) => {
      const mensagem = action.payload;
      const entry = state.byConversaId[mensagem.conversaId] ?? emptyEntry();
      if (!entry.items.find((m) => m.id === mensagem.id)) {
        entry.items = [...entry.items, mensagem].sort((a, b) =>
          a.dataMensagem.localeCompare(b.dataMensagem),
        );
      }
      state.byConversaId[mensagem.conversaId] = entry;
    },
    realtimeMessageStatusUpdated: (
      state,
      action: {
        payload: {
          conversaId: string;
          messageId: string;
          status: MensagemStatus;
          falhaEntrega?: boolean;
          motivoFalha?: string | null;
          podeRetomar?: boolean | null;
          deliveredAt?: string | null;
          readAt?: string | null;
          failedAt?: string | null;
          statusLog?: InternalChatMensagem['statusLog'];
          occurredAt?: string;
        };
      },
    ) => {
      const entry = state.byConversaId[action.payload.conversaId];
      if (!entry) return;
      const message = entry.items.find((m) => m.id === action.payload.messageId);
      if (!message) return;
      if (!canApplyMensagemStatus(message.status, action.payload.status)) return;
      message.status = action.payload.status;
      if (action.payload.falhaEntrega !== undefined) {
        message.falhaEntrega = action.payload.falhaEntrega;
      } else if (action.payload.status === 'FAILED') {
        message.falhaEntrega = true;
      }
      if (action.payload.motivoFalha !== undefined) {
        message.motivoFalha = action.payload.motivoFalha;
      }
      if (action.payload.podeRetomar !== undefined) {
        message.podeRetomar = Boolean(action.payload.podeRetomar);
      }
      if (action.payload.deliveredAt !== undefined) {
        message.deliveredAt = action.payload.deliveredAt;
      }
      if (action.payload.readAt !== undefined) {
        message.readAt = action.payload.readAt;
      }
      if (action.payload.failedAt !== undefined) {
        message.failedAt = action.payload.failedAt;
      }
      if (action.payload.statusLog !== undefined) {
        message.statusLog = action.payload.statusLog;
      } else if (action.payload.occurredAt) {
        const at = action.payload.occurredAt;
        const log = [...(message.statusLog ?? [])];
        const last = log[log.length - 1];
        if (!last || last.status !== action.payload.status) {
          log.push({ status: action.payload.status, at });
          message.statusLog = log;
        }
        if (action.payload.status === 'DELIVERED' && !message.deliveredAt) {
          message.deliveredAt = at;
        }
        if (action.payload.status === 'SEEN' && !message.readAt) {
          message.readAt = at;
        }
        if (action.payload.status === 'FAILED' && !message.failedAt) {
          message.failedAt = at;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMessageHistory.pending, (state, action) => {
        const conversaId = action.meta.arg.conversaId;
        const entry = state.byConversaId[conversaId] ?? emptyEntry();
        entry.isLoading = true;
        entry.error = null;
        state.byConversaId[conversaId] = entry;
      })
      .addCase(fetchMessageHistory.fulfilled, (state, action) => {
        const { conversaId, cursorRequested, data } = action.payload;
        const entry = state.byConversaId[conversaId] ?? emptyEntry();
        entry.isLoading = false;
        entry.nextCursor = data.nextCursor;
        entry.hasMore = data.hasMore;
        if (cursorRequested) {
          const known = new Set(entry.items.map((m) => m.id));
          const merged = [...entry.items, ...data.items.filter((m) => !known.has(m.id))];
          entry.items = merged.sort((a, b) => a.dataMensagem.localeCompare(b.dataMensagem));
        } else {
          const byId = new Map(data.items.map((m) => [m.id, m]));
          for (const local of entry.items) {
            const server = byId.get(local.id);
            if (!server) {
              byId.set(local.id, local);
              continue;
            }
            const preferLocalStatus = canApplyMensagemStatus(server.status, local.status);
            byId.set(local.id, {
              ...server,
              status: preferLocalStatus ? local.status : server.status,
              falhaEntrega: preferLocalStatus ? local.falhaEntrega : server.falhaEntrega,
              motivoFalha: preferLocalStatus
                ? (local.motivoFalha ?? server.motivoFalha)
                : (server.motivoFalha ?? local.motivoFalha),
              podeRetomar: preferLocalStatus
                ? (local.podeRetomar ?? server.podeRetomar)
                : (server.podeRetomar ?? local.podeRetomar),
              deliveredAt: local.deliveredAt ?? server.deliveredAt,
              readAt: local.readAt ?? server.readAt,
              failedAt: local.failedAt ?? server.failedAt,
              statusLog:
                (local.statusLog?.length ?? 0) >= (server.statusLog?.length ?? 0)
                  ? (local.statusLog ?? server.statusLog)
                  : server.statusLog,
            });
          }
          entry.items = [...byId.values()].sort((a, b) =>
            a.dataMensagem.localeCompare(b.dataMensagem),
          );
          entry.historyHydrated = true;
        }
        state.byConversaId[conversaId] = entry;
      })
      .addCase(fetchMessageHistory.rejected, (state, action) => {
        const conversaId = action.meta.arg.conversaId;
        const entry = state.byConversaId[conversaId] ?? emptyEntry();
        entry.isLoading = false;
        entry.error = action.payload ?? 'Erro ao carregar histórico';
        state.byConversaId[conversaId] = entry;
      })
      .addCase(sendMessage.pending, (state) => {
        state.isSending = true;
        state.sendError = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isSending = false;
        const mensagem = action.payload;
        const entry = state.byConversaId[mensagem.conversaId] ?? emptyEntry();
        if (!entry.items.find((m) => m.id === mensagem.id)) {
          entry.items = [...entry.items, mensagem].sort((a, b) =>
            a.dataMensagem.localeCompare(b.dataMensagem),
          );
        }
        state.byConversaId[mensagem.conversaId] = entry;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isSending = false;
        state.sendError = action.payload ?? 'Erro ao enviar mensagem';
      })
      .addCase(enrichRealtimeMessages.fulfilled, (state, action) => {
        const { conversaId, items } = action.payload;
        if (items.length === 0) return;
        const entry = state.byConversaId[conversaId] ?? emptyEntry();
        const byId = new Map(entry.items.map((m) => [m.id, m]));
        for (const incoming of items) {
          const existing = byId.get(incoming.id);
          if (existing) {
            existing.conteudo = incoming.conteudo;
            existing.status = incoming.status;
            existing.falhaEntrega = incoming.falhaEntrega;
            existing.motivoFalha = incoming.motivoFalha;
            existing.podeRetomar = incoming.podeRetomar;
            existing.metadados = incoming.metadados ?? existing.metadados;
            if (incoming.metadata !== undefined) existing.metadata = incoming.metadata;
            existing.anexos = incoming.anexos ?? existing.anexos;
            if (incoming.mediaUrl !== undefined) existing.mediaUrl = incoming.mediaUrl;
          } else {
            byId.set(incoming.id, incoming);
          }
        }
        entry.items = [...byId.values()].sort((a, b) =>
          a.dataMensagem.localeCompare(b.dataMensagem),
        );
        state.byConversaId[conversaId] = entry;
      })
      .addCase(resendTemplate.fulfilled, (state, action) => {
        const mensagem = action.payload;
        const originalId = action.meta.arg;
        const entry = state.byConversaId[mensagem.conversaId] ?? emptyEntry();
        const original = entry.items.find((m) => m.id === originalId);
        if (original) {
          original.metadados = { ...original.metadados, template_enviado: true };
          original.podeRetomar = false;
        }
        if (!entry.items.find((m) => m.id === mensagem.id)) {
          entry.items = [...entry.items, mensagem].sort((a, b) =>
            a.dataMensagem.localeCompare(b.dataMensagem),
          );
        }
        state.byConversaId[mensagem.conversaId] = entry;
      })
      .addCase(resumeConversationTemplate.fulfilled, (state, action) => {
        const mensagem = action.payload;
        const entry = state.byConversaId[mensagem.conversaId] ?? emptyEntry();
        if (!entry.items.find((m) => m.id === mensagem.id)) {
          entry.items = [...entry.items, mensagem].sort((a, b) =>
            a.dataMensagem.localeCompare(b.dataMensagem),
          );
        }
        state.byConversaId[mensagem.conversaId] = entry;
      });
  },
});

export const {
  clearSendError,
  resetConversaMessages,
  realtimeMessageCreated,
  realtimeMessageStatusUpdated,
} = messagesSlice.actions;
export const messagesReducer = messagesSlice.reducer;
export type { MessagesState };
